/* eslint-disable @typescript-eslint/no-use-before-define,@typescript-eslint/no-unused-vars */
import { createEffect, createEvent } from 'effector'
import { ConfirmationResult, getAuth, signInWithPhoneNumber } from 'firebase/auth'

import { $authFirePhone, $authMethod, $authQuery, $name, $phone, $sendCodeType } from './store'
import {
  AuthAPI,
  AuthQuery,
  GetValidatePhoneReq,
  LoginTokenReq,
  PostRegReq,
  PostRegSendCallReq,
  PostRegSendSmsReq,
  PostRegValidateCallReq,
  PostRegValidateReq,
  PostValidateReq,
  SendCodeType,
  ThirdPartyReq,
} from '../../services/api/Auth'
import { APIResult } from '../../shared/types'
import { AuthMethod } from './type'

// import { $activeLanguage } from '../Languages/store'

export enum AuthType {
  NULL,
  REGISTER,
  LOGIN
}

export const $setAuthToken = createEvent<string>()

export const $setAuthType = createEvent<AuthType>()
export const $setPhone = createEvent<string>()
export const $setSendCodeType = createEvent<SendCodeType>()
export const $setCode = createEvent<string>()
export const $setName = createEvent<string>()
export const $setPendingFirebase = createEvent<boolean>()
export const $setPermit = createEvent<boolean>()
export const $setTimeoutSendCode = createEvent()
export const $setAuthMethod = createEvent<AuthMethod>()
export const clearValidateFx = createEvent()
export const $setCodeError = createEvent<string>()
export const setPhoneErrorFx = createEvent<string>()
export const $setAuthQuery = createEvent<AuthQuery>()

export const $authThirdParty = createEffect<ThirdPartyReq, APIResult<typeof AuthAPI['thirdParty']>, APIResult>(
  AuthAPI.thirdParty,
)

export const $authThirdPartyLogin = createEffect<ThirdPartyReq, APIResult<typeof AuthAPI['thirdPartyLogin']>, APIResult>(
  AuthAPI.thirdPartyLogin,
)

export const $loginToken = createEffect<LoginTokenReq, APIResult<typeof AuthAPI['loginToken']>, APIResult>(
  AuthAPI.loginToken,
)

export const $postValidate = createEffect<PostValidateReq, APIResult<typeof AuthAPI['postValidate']>, APIResult>(
  AuthAPI.postValidate,
)

export const $getValidatePhone = createEffect<GetValidatePhoneReq, APIResult<typeof AuthAPI['getValidatePhone']>, APIResult>(
  AuthAPI.getValidatePhone,
)

export const postRegSendCallFx = createEffect<PostRegSendCallReq, APIResult<typeof AuthAPI['postRegSendCall']>, APIResult>(
  AuthAPI.postRegSendCall,
)

export const postRegSendSmsFx = createEffect<PostRegSendSmsReq, APIResult<typeof AuthAPI['postRegSendSms']>, APIResult>(
  AuthAPI.postRegSendSms,
)

export const postRegValidateCallFx = createEffect<PostRegValidateCallReq, APIResult<typeof AuthAPI['postRegValidateCall']>, APIResult>(
  AuthAPI.postRegValidateCall,
)

export const postRegValidateFx = createEffect<PostRegValidateReq, APIResult<typeof AuthAPI['postRegValidate']>, APIResult>(
  AuthAPI.postRegValidate,
)

export const postRegFx = createEffect<PostRegReq, APIResult<typeof AuthAPI['postReg']>, APIResult>(
  AuthAPI.postReg,
)

export const sendCodeFx = createEffect<{ phone: string, type: SendCodeType }, void, unknown>(({
  phone,
  type,
}) => {
  $setTimeoutSendCode()

  if (type === SendCodeType.CALL) {
    postRegSendCallFx({ phone })
  }
  if (type === SendCodeType.SMS) {
    postRegSendSmsFx({ phone })
  }
  if (type === SendCodeType.FIREBASE) {
    firebaseSendCodeFx(phone)
  }
})

export const regSendCodeFx = createEffect<GetValidatePhoneReq, void, APIResult>(
  async ({ phone }) => {
    const name = $name.getState()
    await postRegValidateFx({
      firstname: name,
      phone,
    })

    const { value } = await $getValidatePhone({ phone })
    const { type } = value.data

    $setSendCodeType(type)
    await sendCodeFx({ phone, type })
    $setAuthMethod(AuthMethod.CODE)
  },
)

export const loginSendCodeFx = createEffect<{ phone: string }, void, APIResult>(
  async ({ phone }) => {
    // такой костыль потому что нет роута на беке для чека телефона на нахождение в базе
    try {
      await $postValidate({ phone })
    } catch {
      $setSendCodeType(SendCodeType.FIREBASE)
      await sendCodeFx({ phone, type: SendCodeType.FIREBASE })
      $setAuthMethod(AuthMethod.CODE)
    }
  },
)

export const regValidateCodeFx = createEffect<{code: string}, void, unknown>(async ({
  code,
}) => {
  const type = $sendCodeType.getState()
  const phone = $phone.getState()

  if (type === SendCodeType.CALL) {
    await postRegValidateCallFx({ phone, callCode: code })
  }
  if (type === SendCodeType.SMS) {
    await postRegValidateFx({ smsCode: code })
  }
  if (type === SendCodeType.FIREBASE) {
    await firebaseSendCodeFx(phone)
  }
})

export const regFx = createEffect<{ code: string, languageId: number }, void, unknown>(async ({
  code,
  languageId,
}) => {
  const phone = $phone.getState()
  const name = $name.getState()
  const type = $sendCodeType.getState()
  const query = $authQuery.getState()

  if (type === SendCodeType.CALL || type === SendCodeType.SMS) {
    await regValidateCodeFx({ code })

    await postRegFx({
      firstname: name,
      phone,
      userLanguageId: languageId,
      scenario: 3,
      ...query,
    })
  }
  if (type === SendCodeType.FIREBASE) {
    const accessToken = await firebaseValidationCodeFx({ code })
    await $authThirdParty({
      accessToken,
      username: name,
      ...query,
    })
  }
})

export const loginFx = createEffect<{ code?: string, password?: string }, void, unknown>(async ({
  code = '',
  password = '',
}) => {
  const sendCodeType = $sendCodeType.getState()
  const authMethod = $authMethod.getState()
  const phone = $phone.getState()

  if (authMethod === AuthMethod.CODE) {
    if (sendCodeType === SendCodeType.FIREBASE) {
      const accessToken = await firebaseValidationCodeFx({ code })
      await $authThirdPartyLogin({ accessToken })
    }
  }
  if (authMethod === AuthMethod.PASSWORD) {
    await $loginToken({ password, login: phone })
  }
})

export const firebaseSendCodeFx = createEffect<string, ConfirmationResult, any>(
  (phone) => signInWithPhoneNumber(getAuth(), phone, window.recaptchaVerifier),
)

export const firebaseValidationCodeFx = createEffect<{ code: string }, string, unknown>(
  async ({ code }) => {
    const authFirePhone = $authFirePhone.getState()
    if (!authFirePhone) { throw new Error('Error FB: confirmationResult not found') }
    const { user } = await authFirePhone.confirm(code)
    const { token: accessToken } = await user.getIdTokenResult()
    return accessToken
  },
)
